import { ShowHeader } from "@/components";
import { formatDate } from "@/utils";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { Loading } from "react-admin";
import { useParams } from "react-router-dom";
import {
  PaymentAttempt,
  PaymentEventRow,
  PaymentTransactionSummary,
  TransactionStatus,
} from "@/api/payments";
import { useGetPaymentDetail } from "../../queries";
import {
  eventStatusChipColor,
  formatOrderStatus,
  formatPurchaseKind,
  formatStage,
  orderStatusChipColor,
} from "../../utils";

const EventRow: FC<{ event: PaymentEventRow }> = ({ event }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        borderLeft: "2px solid",
        borderColor: "divider",
        pl: 2,
        py: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 160 }}
        >
          {formatDate(event.createdAt, "MMM D, HH:mm:ss")}
        </Typography>
        <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 500 }}>
          {formatStage(event.stage)}
        </Typography>
        <Chip
          size="small"
          label={event.status}
          color={eventStatusChipColor(event.status)}
        />
        {event.errorCode && (
          <Typography variant="body2" color="error">
            {event.errorCode}
            {event.errorMessage ? `: ${event.errorMessage}` : ""}
          </Typography>
        )}
        <Box flexGrow={1} />
        <IconButton size="small" onClick={() => setOpen((v) => !v)}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.5} mt={1} ml={1}>
          {event.providerTransactionId && (
            <Typography variant="caption">
              Provider transaction ID: <code>{event.providerTransactionId}</code>
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Payload:
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1,
              fontSize: 11,
              overflow: "auto",
              maxHeight: 320,
            }}
          >
            {JSON.stringify(event.payload, null, 2)}
          </Box>
        </Stack>
      </Collapse>
    </Box>
  );
};

const TransactionRow: FC<{ tx: PaymentTransactionSummary }> = ({ tx }) => (
  <Box
    sx={{
      borderLeft: "2px solid",
      borderColor: "divider",
      pl: 2,
      py: 1,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
      <Chip
        size="small"
        label={tx.status}
        color={tx.status === TransactionStatus.COMPLETED ? "success" : "error"}
      />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {tx.processor}
      </Typography>
      <Typography variant="body2">
        {`${tx.actualCurrency ?? "$"}${(tx.actualAmount / 100).toFixed(2)}`}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatDate(tx.createdAt, "MMM D, YYYY HH:mm")}
      </Typography>
      {tx.errorCode && (
        <Typography variant="body2" color="error">
          {tx.errorCode}
          {tx.errorMessage ? `: ${tx.errorMessage}` : ""}
        </Typography>
      )}
    </Stack>
    {tx.providerTransactionId && (
      <Typography variant="caption" color="text.secondary">
        Provider txn ID: <code>{tx.providerTransactionId}</code>
      </Typography>
    )}
  </Box>
);

const AttemptBlock: FC<{ attempt: PaymentAttempt; index: number }> = ({
  attempt,
  index,
}) => {
  const latestEvent = attempt.events[attempt.events.length - 1];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Attempt #{index + 1}
        </Typography>
        <Chip
          size="small"
          label={latestEvent?.status ?? "—"}
          color={
            latestEvent ? eventStatusChipColor(latestEvent.status) : "default"
          }
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {attempt.attemptId}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        {attempt.events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </Stack>
    </Paper>
  );
};

const PaymentShow: FC = () => {
  const { id } = useParams();
  const { payment, isPending } = useGetPaymentDetail(id ?? "");

  if (isPending) return <Loading />;
  if (!payment) return <Typography>Payment not found</Typography>;

  // Derive label inputs from raw products. BE sends raw data so the show
  // view can compose whatever it needs without a backend round-trip.
  const isRecurringProduct = payment.products.some((product) =>
    product.items.some((item) => !item.isOneTime),
  );
  // Multi-product orders aren't created today (payment-link always sends one),
  // but the data model supports them. Render the list faithfully if it grows.
  const productSummary = payment.products
    .map((product) =>
      product.quantity > 1 ? `${product.name} × ${product.quantity}` : product.name,
    )
    .join(", ");

  return (
    <Stack direction="column" p={2} spacing={2}>
      <ShowHeader title="Payment" />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Typography variant="h6">Order</Typography>
          <Chip
            size="small"
            label={formatOrderStatus(payment.status)}
            color={orderStatusChipColor(payment.status)}
          />
          <Chip
            size="small"
            variant="outlined"
            label={formatPurchaseKind(payment.type, isRecurringProduct)}
          />
        </Stack>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Field label="Order ID" value={payment.orderId} mono />
          <Field
            label="Created"
            value={formatDate(payment.createdAt, "MMM D, YYYY HH:mm")}
          />
          {payment.paidAt && (
            <Field
              label="Paid at"
              value={formatDate(payment.paidAt, "MMM D, YYYY HH:mm")}
            />
          )}
          <Field
            label="Amount"
            value={`$${(payment.price / 100).toFixed(2)}`}
          />
          {productSummary && (
            <Field
              label={payment.products.length > 1 ? "Products" : "Product"}
              value={productSummary}
            />
          )}
          {payment.user?.email && (
            <Field label="User" value={payment.user.email} />
          )}
        </Stack>
      </Paper>

      {payment.transactions.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <Typography variant="h6">
              Transactions ({payment.transactions.length})
            </Typography>
            {payment.transactions.filter(
              (tx) => tx.status === TransactionStatus.COMPLETED,
            ).length > 1 && (
              <Chip
                size="small"
                color="warning"
                label="Multiple successful transactions"
              />
            )}
          </Stack>
          <Stack spacing={1.5}>
            {payment.transactions.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} />
            ))}
          </Stack>
        </Paper>
      )}

      <Divider />
      <Typography variant="h6">
        Attempts ({payment.attempts.length})
      </Typography>
      <Stack spacing={2}>
        {payment.attempts.map((attempt, idx) => (
          <AttemptBlock
            key={attempt.attemptId}
            attempt={attempt}
            index={idx}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const Field: FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Stack>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={mono ? { fontFamily: "monospace" } : {}}>
      {value}
    </Typography>
  </Stack>
);

export { PaymentShow };
